import { create } from 'zustand';
import {
  PMNode, Process, Person, Cursor, ViewBox,
  NodeType, SessionState, SegmentStatement,
  BASE_VB, PALETTE,
  canvasNodeToProcessNode,
} from '../types';
import type { Segment } from '../migrations/legacyEdge';
import { ringAt } from '../utils/geometry';
import { impactScore, impactColor, impactLabel } from '../utils/impact';
import { validateProcessSegments } from '../utils/processValidation';
import { buildEdgeMap, makeStableEdgeId, resolveProcessEdges } from '../utils/processEdges';
import { canMutateWithIssues } from '../utils/validationPolicy';

export { ringAt, impactScore, impactColor, impactLabel };

// ── Tipos del store ──────────────────────────────────────────────────────────

interface SessionStore {
  // ── Estado del canvas ──
  nodes:          Record<string, PMNode>;
  processes:      Process[];
  edges:          Segment[];
  people:         Person[];
  viewBox:        ViewBox;
  ringScale:      number;
  nodeCounter:    number;

  // ── Estado de UI / interacción ──
  activeProcessId:  number | null;
  selectedNodeId:   string | null;
  hint:             string;
  lastValidationWarning: string | null;
  cursors:          Record<string, Cursor>;   // para colaboración futura

  // ── Acciones: nodos ──
  addNode: (label: string, x: number, y: number, type: NodeType, subtype: PMNode['subtype'], extra?: Partial<PMNode>) => string;
  updateNode: (id: string, patch: Partial<Omit<PMNode, 'type' | 'subtype'>> & { type?: NodeType; subtype?: PMNode['subtype'] }) => void;
  removeNode: (id: string) => void;

  // ── Acciones: personas ──
  addPerson: (name: string) => void;
  removePerson: (id: string) => void;

  // ── Acciones: procesos ──
  createProcess: (name: string, color: string) => void;
  reorderProcess: (fromIdx: number, toIdx: number) => void;
  deleteProcess: (id: number) => void;
  renameProcess: (id: number, name: string) => void;
  toggleProcessVisibility: (id: number) => void;
  setActiveProcess: (id: number | null) => void;
  finishProcess: () => void;
  reopenProcess: (id: number) => void;

  // ── Acciones: segmentos ──
  addSegment: (procId: number, seg: Partial<Segment> & { from: string; to: string }) => number;
  updateSegment: (procId: number, segIdx: number, patch: Partial<Segment>) => void;
  deleteSegment: (procId: number, segIdx: number) => void;
  reorderSegment: (procId: number, from: number, to: number) => void;

  // ── Acciones: selección ──
  selectNode: (id: string | null) => void;
  handleNodeClick: (id: string) => void;

  // ── Acciones: viewBox ──
  setViewBox: (vb: ViewBox | ((prev: ViewBox) => ViewBox)) => void;
  setRingScale: (scale: number) => void;

  // ── Acciones: hint ──
  setHint: (text: string) => void;
  setLastValidationWarning: (warning: string | null) => void;

  // ── Acciones: cursores (colaboración) ──
  setCursor: (uid: string, cursor: Cursor) => void;
  removeCursor: (uid: string) => void;

  // ── Save / Load ──
  getState: () => SessionState;
  loadState: (state: SessionState) => void;
  resetState: () => void;

  // ── Color activo (UI nueva sesión) ──
  activeColor: string;
  setActiveColor: (color: string) => void;
}

// ── Segmento por defecto ─────────────────────────────────────────────────────

const defaultSegment = (): Omit<Segment, 'id' | 'from' | 'to'> => ({
  friction: 'none',
  blocking: 'blocking',
  freq:     'adhoc',
  dur:      'mins',
  pred:     'yes',
  flowType: 'normal',
});



function buildStatementFromSegment(seg: Partial<Segment> & { from: string; to: string }): SegmentStatement {
  const incoming = seg.statement;
  if (incoming) return incoming;
  return {
    type: 'STEP',
    from: seg.from,
    to: seg.to,
    action: seg.label,
    sync: seg.blocking,
    meta: {},
  };
}

function normalizeSegment(seg: Partial<Segment> & { from: string; to: string }): Segment {
  const defaults = defaultSegment();
  const statement = buildStatementFromSegment(seg);
  const withLegacy = { ...defaults, ...seg };
  return {
    ...withLegacy,
    statement,
    id: seg.id ?? withLegacy.id ?? '',
  };
}

function processSegments(proc: Process, edgeMap: Record<string, Segment>): Segment[] {
  return resolveProcessEdges(proc, edgeMap);
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useSessionStore = create<SessionStore>((set, get) => ({
  // ── Estado inicial ──────────────────────────────────────────────────────────
  nodes:           {},
  processes:       [],
  edges:           [],
  people:          [],
  viewBox:         { ...BASE_VB },
  ringScale:       1,
  nodeCounter:     0,
  activeProcessId: null,
  selectedNodeId:  null,
  hint:            'Clic derecho en el lienzo para añadir nodos',
  lastValidationWarning: null,
  cursors:         {},
  activeColor:     PALETTE[0],

  // ── Nodos ───────────────────────────────────────────────────────────────────

  addNode: (label, x, y, type, subtype, extra = {}) => {
    const { nodeCounter } = get();
    const id = `${type}_${nodeCounter + 1}`;
    const node: PMNode = { id, label, x, y, type, subtype, updatedAt: Date.now(), ...extra };
    set(s => ({
      nodes:       { ...s.nodes, [id]: node },
      nodeCounter: s.nodeCounter + 1,
    }));
    return id;
  },

  updateNode: (id, patch) => {
    set(s => ({
      nodes: { ...s.nodes, [id]: { ...s.nodes[id], ...patch, updatedAt: Date.now() } },
    }));
  },

  removeNode: (id) => {
    set(s => {
      let nextWarning: string | null = null;
      const nodes = { ...s.nodes };
      delete nodes[id];
      const edges = s.edges.filter(edge => edge.from !== id && edge.to !== id);
      const edgeMap = buildEdgeMap(edges);
      const processes = s.processes.map(proc => {
        const segments = processSegments(proc, edgeMap);
        const validation = validateProcessSegments({ segments });
        const policy = canMutateWithIssues('node.remove', validation.issues, { processName: proc.name });
        if (policy.warning && !nextWarning) nextWarning = policy.warning;
        return { ...proc, edgeIds: segments.map(seg => seg.id) };
      });
      const people = s.people.filter(p => p.id !== id);
      return { nodes, edges, processes, people, lastValidationWarning: nextWarning };
    });
  },

  // ── Personas ────────────────────────────────────────────────────────────────

  addPerson: (name) => {
    if (!name.trim()) return;
    const { people, addNode } = get();
    const count    = people.length;
    const a        = (Math.PI * 2 * count) / Math.max(8, count) - Math.PI / 2;
    const r        = count === 0 ? 0 : 42;
    const initials = name.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    const id       = addNode(name.trim(), r * Math.cos(a), r * Math.sin(a), 'actor', 'person', { initials });
    set(s => ({ people: [...s.people, { id, name: name.trim() }] }));
  },

  removePerson: (id) => {
    get().removeNode(id);
    // removeNode ya filtra peopleList si quisieras, pero lo hacemos explícito:
    set(s => ({ people: s.people.filter(p => p.id !== id) }));
  },

  // ── Procesos ────────────────────────────────────────────────────────────────

  createProcess: (name, color) => {
    const proc: Process = {
      id:                 Date.now(),
      name,
      color,
      visible:            true,
      dslSchemaVersion:   1,
      edgeIds:            [],
    };
    set(s => ({
      processes:       [...s.processes, proc],
      activeProcessId: proc.id,
      selectedNodeId:  null,
    }));
    get().setHint(`"${name}" iniciado — haz clic en el primer nodo`);
  },

  deleteProcess: (id) => {
    set(s => ({
      processes:       s.processes.filter(p => p.id !== id),
      activeProcessId: s.activeProcessId === id ? null : s.activeProcessId,
      selectedNodeId:  s.activeProcessId === id ? null : s.selectedNodeId,
    }));
  },

  renameProcess: (id, name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    set(s => ({
      processes: s.processes.map(p =>
        p.id === id ? { ...p, name: trimmed } : p
      ),
    }));
  },

  reorderProcess: (fromIdx, toIdx) => {
    set(s => {
      if (
        fromIdx < 0 ||
        toIdx < 0 ||
        fromIdx >= s.processes.length ||
        toIdx >= s.processes.length ||
        fromIdx === toIdx
      ) {
        return s;
      }
      const processes = [...s.processes];
      const [moved] = processes.splice(fromIdx, 1);
      processes.splice(toIdx, 0, moved);
      return { processes };
    });
  },

  toggleProcessVisibility: (id) => {
    set(s => ({
      processes: s.processes.map(p =>
        p.id === id ? { ...p, visible: !p.visible } : p
      ),
    }));
  },

  setActiveProcess: (id) => {
    set({ activeProcessId: id, selectedNodeId: null });
  },

  finishProcess: () => {
    set({ activeProcessId: null, selectedNodeId: null });
    get().setHint('Proceso cerrado — pulsa "Seguir editando" para reabrirlo');
  },

  reopenProcess: (id) => {
    const proc = get().processes.find(p => p.id === id);
    if (!proc) return;
    set({ activeProcessId: id, selectedNodeId: null });
    get().setHint(`Editando "${proc.name}" — haz clic en un nodo para continuar`);
  },

  // ── Segmentos ───────────────────────────────────────────────────────────────

  addSegment: (procId, seg) => {
    // Transición v1 → layout jerárquico:
    // todo segmento nuevo se normaliza para incluir metadatos estructurales
    // (blockRole/blockId/parentBlockId/operatorKind) sin romper callers antiguos.
    const full = normalizeSegment(seg);
    let newIdx = -1;
    set(s => {
      const edgeId = full.id || `edge_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const edge: Segment = { ...full, id: edgeId };
      const processes = s.processes.map(p => {
        if (p.id !== procId) return p;
        const edgeIds = [...p.edgeIds, edge.id];
        newIdx = edgeIds.length - 1;
        return { ...p, edgeIds };
      });
      return { processes, edges: [...s.edges, edge] };
    });
    return newIdx;
  },

  updateSegment: (procId, segIdx, patch) => {
    set(s => {
      const proc = s.processes.find(p => p.id === procId);
      if (!proc) return s;
      const targetEdgeId = proc.edgeIds[segIdx];
      if (!targetEdgeId) return s;
      const edges = s.edges.map(edge => {
        if (edge.id !== targetEdgeId) return edge;
        const nextStatement = patch.statement ?? edge.statement;
        const merged = { ...edge, ...patch, statement: nextStatement } as Record<string, unknown>;
        Object.keys(merged).forEach(key => {
          if (merged[key] === undefined) delete merged[key];
        });
        return normalizeSegment(merged as Partial<Segment> & { from: string; to: string });
      });
      return { edges };
    });
  },

  deleteSegment: (procId, segIdx) => {
    set(s => {
      let nextWarning: string | null = null;
      let removedEdgeId: string | null = null;
      const processes = s.processes.map(p => {
        if (p.id !== procId) return p;
        const edgeIds = p.edgeIds.filter((edgeId, i) => {
          if (i === segIdx) {
            removedEdgeId = edgeId;
            return false;
          }
          return true;
        });
        const edgeMap = buildEdgeMap(s.edges);
        const segments = edgeIds.map(edgeId => edgeMap[edgeId]).filter((edge): edge is Segment => Boolean(edge));
        const validation = validateProcessSegments({ segments });
        const policy = canMutateWithIssues('segment.delete', validation.issues, { processName: p.name });
        if (policy.warning) nextWarning = policy.warning;
        return { ...p, edgeIds };
      });
      const edges = removedEdgeId ? s.edges.filter(edge => edge.id !== removedEdgeId) : s.edges;
      return { processes, edges, lastValidationWarning: nextWarning };
    });
  },

  reorderSegment: (procId, fromIdx, toIdx) => {
    set(s => {
      let nextWarning: string | null = null;
      const processes = s.processes.map(p => {
        if (p.id !== procId) return p;
        const edgeIds = [...p.edgeIds];
        if (
          fromIdx < 0 ||
          toIdx < 0 ||
          fromIdx >= edgeIds.length ||
          toIdx >= edgeIds.length ||
          fromIdx === toIdx
        ) {
          return p;
        }
        const [moved]  = edgeIds.splice(fromIdx, 1);
        edgeIds.splice(toIdx, 0, moved);
        const edgeMap = buildEdgeMap(s.edges);
        const segments = edgeIds.map(edgeId => edgeMap[edgeId]).filter((edge): edge is Segment => Boolean(edge));
        const validation = validateProcessSegments({ segments });
        const policy = canMutateWithIssues('segment.reorder', validation.issues, { processName: p.name });
        if (policy.warning) nextWarning = policy.warning;
        return { ...p, edgeIds };
      });
      return { processes, lastValidationWarning: nextWarning };
    });
  },

  // ── Selección ───────────────────────────────────────────────────────────────

  selectNode: (id) => {
    set({ selectedNodeId: id });
  },

  handleNodeClick: (id) => {
    const {
      activeProcessId,
      selectedNodeId,
      processes,
      nodes,
      addSegment,
      setHint,
    } = get();

    if (!activeProcessId) {
      setHint('Primero inicia un proceso →');
      return;
    }
    const proc = processes.find(p => p.id === activeProcessId);
    if (!proc) return;

    if (selectedNodeId) {
      if (selectedNodeId === id) {
        // Deseleccionar
        set({ selectedNodeId: null });
        setHint('Haz clic en el siguiente nodo');
        return;
      }
      // Conectar
      addSegment(activeProcessId, { from: selectedNodeId, to: id });
      set({ selectedNodeId: id });   // mantener destino seleccionado
      setHint('Rellena el hilo → después elige el siguiente nodo');
      // El componente Canvas escuchará este cambio y abrirá el SegmentEditor
      // mediante un efecto reactivo sobre `lastAddedSegment` (ver Canvas.tsx)
    } else {
      set({ selectedNodeId: id });
      const label = nodes[id]?.label ?? id;
      setHint(`"${label}" seleccionado → elige el siguiente nodo`);
    }
  },

  // ── ViewBox ─────────────────────────────────────────────────────────────────

  setViewBox: (vb) => {
    if (typeof vb === 'function') {
      set(s => ({ viewBox: vb(s.viewBox) }));
    } else {
      set({ viewBox: vb });
    }
  },
  setRingScale: (scale) => {
    set({ ringScale: Math.max(1, scale) });
  },

  // ── Hint ────────────────────────────────────────────────────────────────────

  setHint: (text) => set({ hint: text }),
  setLastValidationWarning: (warning) => set({ lastValidationWarning: warning }),

  // ── Cursores ─────────────────────────────────────────────────────────────────

  setCursor: (uid, cursor) =>
    set(s => ({ cursors: { ...s.cursors, [uid]: cursor } })),

  removeCursor: (uid) =>
    set(s => {
      const cursors = { ...s.cursors };
      delete cursors[uid];
      return { cursors };
    }),

  // ── Color activo ─────────────────────────────────────────────────────────────

  setActiveColor: (color) => set({ activeColor: color }),

  // ── Save / Load ──────────────────────────────────────────────────────────────

  getState: () => {
    const { nodes, processes, edges, people, viewBox } = get();
    return {
      version:   1,
      nodes:     Object.values(nodes).map(canvasNodeToProcessNode),
      edges,
      layout: {
        nodes: Object.values(nodes),
        viewBox,
      },
      processes,
      people,
    };
  },

  loadState: (state) => {
    const prevState = get();
    const nodeMap: Record<string, PMNode> = {};
    let maxCounter = 0;
    const layoutNodes = state.layout.nodes;
    layoutNodes.forEach(n => {
      const localNode = prevState.nodes[n.id];
      const localTs = localNode?.updatedAt ?? 0;
      const remoteTs = n.updatedAt ?? 0;
      nodeMap[n.id] = localTs > remoteTs ? localNode : n;
      const num = parseInt(n.id.split('_').pop() ?? '0', 10);
      if (!isNaN(num) && num >= maxCounter) maxCounter = num + 1;
    });

    // Transición v1 → layout jerárquico:
    // al cargar estados antiguos, rellenamos campos nuevos de Segment con defaults
    // e inferimos operatorKind desde flowType cuando no existe.
    type LegacyProcessInput = Process & {
      segments?: Array<Partial<Segment> & { from: string; to: string }>;
    };

    const legacyProcesses = (state.processes ?? []) as LegacyProcessInput[];
    const incomingEdges = (state.edges ?? []).map(edge =>
      normalizeSegment(edge as Partial<Segment> & { from: string; to: string }),
    );

    const migratedEdges = [...incomingEdges];
    const nextProcesses = legacyProcesses.map(proc => {
      const hasEdgeIds = Array.isArray(proc.edgeIds);
      if (hasEdgeIds) {
        return {
          ...proc,
          edgeIds: (proc.edgeIds ?? []).filter(edgeId => migratedEdges.some(edge => edge.id === edgeId)),
        };
      }

      const legacySegments = (proc.segments ?? []).map(seg => normalizeSegment(seg as Partial<Segment> & { from: string; to: string }));
      const edgeIds = legacySegments.map((seg, segIdx) => {
        const stableId = makeStableEdgeId(proc.id, segIdx, seg);
        migratedEdges.push({ ...seg, id: stableId });
        return stableId;
      });
      return {
        ...proc,
        edgeIds,
      };
    });
    const nextActiveProcessId =
      prevState.activeProcessId !== null &&
      nextProcesses.some(p => p.id === prevState.activeProcessId)
        ? prevState.activeProcessId
        : null;

    const nextSelectedNodeId =
      prevState.selectedNodeId &&
      !!nodeMap[prevState.selectedNodeId]
        ? prevState.selectedNodeId
        : null;

    set({
      nodes:           nodeMap,
      processes:       nextProcesses,
      edges:           migratedEdges,
      people:          state.people    ?? [],
      viewBox:         state.layout.viewBox,
      ringScale:       prevState.ringScale ?? 1,
      nodeCounter:     maxCounter,
      activeProcessId: nextActiveProcessId,
      selectedNodeId:  nextSelectedNodeId,
      hint:            nextActiveProcessId !== null
        ? prevState.hint
        : 'Mapa cargado ✓',
      lastValidationWarning: null,
    });
  },

  resetState: () => {
    set({
      nodes:           {},
      processes:       [],
      edges:           [],
      people:          [],
      viewBox:         { ...BASE_VB },
      ringScale:       1,
      nodeCounter:     0,
      activeProcessId: null,
      selectedNodeId:  null,
      hint:            'Clic derecho en el lienzo para añadir nodos',
      lastValidationWarning: null,
    });
  },
}));
